import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { hooks } from '../hooks';
import { text } from '../text';
import { theme } from '../constants';
import { components } from '../components';
import { custom } from '../custom';
import { useLanguage } from '../context/LanguageContext';
import { MdSearch, MdMail, MdDrafts, MdPerson } from 'react-icons/md';

const SearchIcon = MdSearch as any;
const MailIcon = MdMail as any;
const DraftsIcon = MdDrafts as any;
const PersonIcon = MdPerson as any;

export const Inbox: React.FC = () => {
  const { pathname } = useLocation();
  const navigate = hooks.useAppNavigate();
  const [activeTab, setActiveTab] = useState<'all' | 'unread'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const { t } = useLanguage();

  useEffect(() => {
    setTimeout(() => {
      window.scroll({ top: -1, left: 0, behavior: 'smooth' });
    }, 10);
  }, [pathname]);

  // Dummy data simulasi untuk Kotak Masuk
  const messages = [
    {
      id: 1,
      sender: 'HR Department',
      subject: 'Pembaruan Kebijakan Cuti Tahunan',
      snippet: 'Halo, kami ingin menginformasikan adanya pembaruan terkait kebijakan cuti tahunan perusahaan yang akan berlaku mulai bulan depan...',
      time: '10:30 AM',
      isUnread: true,
    },
    {
      id: 2,
      sender: 'IT Support',
      subject: 'Jadwal Maintenance Server',
      snippet: 'Diberitahukan kepada seluruh karyawan bahwa akan ada maintenance server pada hari Sabtu, 24 Juli 2026 dari jam 00:00 - 04:00...',
      time: 'Kemarin',
      isUnread: true,
    },
    {
      id: 3,
      sender: 'Finance Team',
      subject: 'Slip Gaji Bulan Juni 2026 Tersedia',
      snippet: 'Slip gaji Anda untuk periode Juni 2026 sudah dapat diunduh melalui aplikasi pada menu Slip Gaji. Jika ada pertanyaan silakan hubungi tim Finance.',
      time: '2 Hari lalu',
      isUnread: false,
    },
    {
      id: 4,
      sender: 'Project Manager',
      subject: 'Undangan Rapat Evaluasi Q2',
      snippet: 'Mohon kehadirannya pada rapat evaluasi Q2 yang akan diselenggarakan secara online pada tautan berikut. Mohon siapkan presentasi masing-masing...',
      time: '14 Jun',
      isUnread: false,
    }
  ];

  const filteredMessages = messages.filter(msg => {
    if (activeTab === 'unread' && !msg.isUnread) return false;
    if (searchQuery && !msg.subject.toLowerCase().includes(searchQuery.toLowerCase()) && !msg.sender.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    return true;
  });

  const renderHeader = () => (
    <components.Header title={t.inbox?.title || "Kotak Masuk"} goBack={true} />
  );

  const renderContent = () => (
    <main className="container" style={{ marginTop: 70, paddingBottom: 40 }}>
      {/* Search Bar */}
      <div style={{ marginBottom: 20 }}>
        <custom.InputField
          placeholder={t.inbox?.searchPlaceholder || "Cari pesan..."}
          value={searchQuery}
          onChange={(e: any) => setSearchQuery(e.target.value)}
          leftIcon={<SearchIcon size={20} color="#9CA3AF" />}
          containerStyle={{ marginBottom: 0 }}
        />
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 24 }}>
        <div 
          onClick={() => setActiveTab('all')}
          style={{
            padding: '8px 16px', borderRadius: 20, cursor: 'pointer', transition: 'all 0.2s', display: 'flex', alignItems: 'center', gap: 6,
            backgroundColor: activeTab === 'all' ? theme.colors.mainDark : '#F3F4F6',
            color: activeTab === 'all' ? 'white' : '#4B5563',
          }}
        >
          <MailIcon size={16} />
          <text.T14 style={{ color: 'inherit', fontWeight: activeTab === 'all' ? '600' : '400' }}>{t.inbox?.allMessages || 'Semua Pesan'}</text.T14>
        </div>
        <div 
          onClick={() => setActiveTab('unread')}
          style={{
            padding: '8px 16px', borderRadius: 20, cursor: 'pointer', transition: 'all 0.2s', display: 'flex', alignItems: 'center', gap: 6,
            backgroundColor: activeTab === 'unread' ? theme.colors.mainColor : '#F3F4F6',
            color: activeTab === 'unread' ? 'white' : '#4B5563',
          }}
        >
          <DraftsIcon size={16} />
          <text.T14 style={{ color: 'inherit', fontWeight: activeTab === 'unread' ? '600' : '400' }}>{t.inbox?.unreadMessages || 'Belum Dibaca'}</text.T14>
        </div>
      </div>

      {/* Messages List */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {filteredMessages.length > 0 ? (
          filteredMessages.map(msg => (
            <div
              key={msg.id}
              style={{
                backgroundColor: msg.isUnread ? '#EFF6FF' : 'white', borderRadius: 16, padding: 16,
                border: msg.isUnread ? '1px solid #BFDBFE' : '1px solid #F3F4F6',
                boxShadow: '0 4px 15px rgba(0,0,0,0.03)', cursor: 'pointer',
                display: 'flex', gap: 14, alignItems: 'flex-start', position: 'relative'
              }}
            >
              {msg.isUnread && (
                <div style={{ position: 'absolute', top: 16, right: 16, width: 10, height: 10, borderRadius: 5, backgroundColor: '#3B82F6' }} />
              )}
              <div style={{
                width: 46, height: 46, borderRadius: 23, flexShrink: 0,
                backgroundColor: msg.isUnread ? '#DBEAFE' : '#F3F4F6',
                display: 'flex', justifyContent: 'center', alignItems: 'center'
              }}>
                <PersonIcon size={24} color={msg.isUnread ? '#2563EB' : '#9CA3AF'} />
              </div>
              <div style={{ flex: 1, minWidth: 0, paddingRight: 10 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4, alignItems: 'center' }}>
                  <text.H6 style={{ color: theme.colors.mainDark, fontSize: 15 }} numberOfLines={1}>{msg.sender}</text.H6>
                  <text.T14 style={{ color: msg.isUnread ? '#3B82F6' : '#9CA3AF', fontSize: 11, fontWeight: msg.isUnread ? '600' : '400' }}>{msg.time}</text.T14>
                </div>
                <text.T14 style={{ fontWeight: msg.isUnread ? '700' : '500', color: theme.colors.mainDark, marginBottom: 4, fontSize: 13 }} numberOfLines={1}>{msg.subject}</text.T14>
                <text.T14 style={{ color: '#6B7280', fontSize: 13, lineHeight: 1.4 }} numberOfLines={2}>{msg.snippet}</text.T14>
              </div>
            </div>
          ))
        ) : (
          <div style={{ textAlign: 'center', padding: '60px 20px', opacity: 0.6 }}>
            <MailIcon size={64} color="#9CA3AF" style={{ marginBottom: 16 }} />
            <text.H5 style={{ color: theme.colors.mainDark, marginBottom: 8 }}>{t.inbox?.noMessages || 'Tidak ada pesan'}</text.H5>
            <text.T14 style={{ color: '#6B7280' }}>{t.inbox?.emptyInbox || 'Kotak masuk Anda saat ini kosong.'}</text.T14>
          </div>
        )}
      </div>
    </main>
  );

  return <div id="screen" style={{ backgroundColor: '#FFFFFF', minHeight: '100vh' }}>{renderHeader()}{renderContent()}</div>;
};